import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { cloudTerminalService } from '../../services/cloud-terminal-service.js';
import { wsClient } from '../../services/websocket-client.js';

const COMPASS_TERMINAL_THEME = {
  background: '#0F1F33',
  foreground: '#E8EDF2',
  cursor: '#00D4FF',
  selectionBackground: '#1E3A5F',
  black: '#0B1929',
  red: '#EF4444',
  green: '#22C55E',
  yellow: '#F59E0B',
  blue: '#00D4FF',
  magenta: '#A855F7',
  cyan: '#06B6D4',
  white: '#E8EDF2',
  brightBlack: '#64748B',
  brightRed: '#F87171',
  brightGreen: '#4ADE80',
  brightYellow: '#FBBF24',
  brightBlue: '#33DDFF',
  brightMagenta: '#C084FC',
  brightCyan: '#22D3EE',
  brightWhite: '#FFFFFF',
};

@customElement('cp-cloud-terminal')
export class CpCloudTerminal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) sessionId = '';
  @property({ type: Boolean }) active = false;

  @state() private _error = '';

  private _terminal: Terminal | null = null;
  private _fitAddon: FitAddon | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _wsHandler = this._onWsMessage.bind(this);
  private _initialized = false;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._dispose();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('sessionId') && this.sessionId && !this._initialized) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._initTerminal();
        });
      });
    }

    if (changed.has('active') && this.active && this._fitAddon && this._terminal) {
      // Re-fit when tab becomes active (container might have changed size)
      requestAnimationFrame(() => {
        this._fitAddon?.fit();
        this._sendResize();
        this._terminal?.focus();
      });
    }
  }

  private _initTerminal(): void {
    const container = this.querySelector('.cp-cloud-terminal__xterm');
    if (!container || this._initialized) return;

    const el = container as HTMLElement;
    if (el.offsetWidth === 0 || el.offsetHeight === 0) {
      setTimeout(() => this._initTerminal(), 100);
      return;
    }

    this._terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 14,
      lineHeight: 1.5,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      theme: COMPASS_TERMINAL_THEME,
      scrollback: 10000,
      convertEol: true,
      allowProposedApi: true,
    });

    this._fitAddon = new FitAddon();
    this._terminal.loadAddon(this._fitAddon);
    this._terminal.open(el);
    this._fitAddon.fit();
    this._sendResize();

    this._terminal.onData((data: string) => {
      if (this.sessionId) {
        cloudTerminalService.sendInput(this.sessionId, data);
      }
    });

    this._resizeObserver = new ResizeObserver(() => {
      if (!this._fitAddon || !this._terminal) return;
      const c = container as HTMLElement;
      if (c.offsetWidth === 0 || c.offsetHeight === 0) return;
      this._fitAddon.fit();
      this._sendResize();
    });
    this._resizeObserver.observe(container);

    this._initialized = true;

    // Request buffered output if reconnecting
    cloudTerminalService.requestBuffer(this.sessionId);
  }

  private _sendResize(): void {
    if (!this._terminal || !this.sessionId) return;
    cloudTerminalService.sendResize(this.sessionId, this._terminal.cols, this._terminal.rows);
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: Record<string, unknown> };

    switch (msg.type) {
      case 'cloud-terminal:data': {
        const payload = msg.payload as { sessionId: string; data: string };
        if (payload.sessionId === this.sessionId && this._terminal) {
          this._terminal.write(payload.data);
        }
        break;
      }

      case 'cloud-terminal:buffer': {
        const payload = msg.payload as { sessionId: string; data: string };
        if (payload.sessionId === this.sessionId && this._terminal && payload.data) {
          this._terminal.write(payload.data);
        }
        break;
      }

      case 'cloud-terminal:error': {
        const payload = msg.payload as { sessionId: string; error: string };
        if (payload.sessionId === this.sessionId || !payload.sessionId) {
          this._error = payload.error;
        }
        break;
      }

      case 'cloud-terminal:closed': {
        const payload = msg.payload as { sessionId: string };
        if (payload.sessionId === this.sessionId) {
          this._terminal?.write('\r\n\x1b[33m[Session ended]\x1b[0m\r\n');
          this.dispatchEvent(new CustomEvent('session-closed', {
            detail: { sessionId: this.sessionId },
            bubbles: true, composed: true,
          }));
        }
        break;
      }
    }
  }

  focusTerminal(): void {
    this._terminal?.focus();
  }

  private _dispose(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._terminal?.dispose();
    this._terminal = null;
    this._fitAddon = null;
    this._initialized = false;
  }

  render() {
    return html`
      <div class="cp-cloud-terminal">
        ${this._error ? html`
          <div class="cp-cloud-terminal__error">${this._error}</div>
        ` : nothing}
        ${!this.sessionId ? html`
          <div class="cp-cloud-terminal__placeholder">
            <span>Warte auf Session...</span>
          </div>
        ` : html`
          <div class="cp-cloud-terminal__xterm"></div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cloud-terminal': CpCloudTerminal;
  }
}
